import type { UserPublicID } from "@atoms/atom-types";
import { useEffect, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import type { NoAccessDetails } from "../FrontendProvision";
import { getBackendUrl } from "../getBackendUrl";
import { useUserStore } from "./useMyUserId";

type UseHandleOidcEventsOptions = Readonly<{
  disableAuthFlows?: boolean;
}>;

export type OidcUserResolutionStatus =
  | "idle"
  | "resolving"
  | "resolved"
  | "no-access"
  | "error";

export interface UseHandleOidcEventsResult {
  status: OidcUserResolutionStatus;
  noAccessDetails: NoAccessDetails | null;
  error: Error | null;
}

export const useHandleOidcEvents = (
  options?: UseHandleOidcEventsOptions,
): UseHandleOidcEventsResult => {
  const disableAuthFlows = options?.disableAuthFlows ?? false;
  const { backendBaseUrl } = getBackendUrl();
  const atomUserEndpoint = `${backendBaseUrl.replace(/\/$/, "")}/auth/me`;
  const setUserId = useUserStore((state) => state.setUserId);
  const {
    isAuthenticated,
    activeNavigator,
    isLoading,
    signinRedirect,
    user,
    events,
    signinSilent,
    error,
    signoutRedirect,
  } = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [status, setStatus] = useState<OidcUserResolutionStatus>("idle");
  const [noAccessDetails, setNoAccessDetails] =
    useState<NoAccessDetails | null>(null);
  const [resolutionError, setResolutionError] = useState<Error | null>(null);

  // Update user ID when authenticated using backend /auth/me.
  useEffect(() => {
    let cancelled = false;
    const syncUserId = async () => {
      if (!isAuthenticated || !user) {
        if (!cancelled) {
          setUserId(null);
          setStatus("idle");
          setNoAccessDetails(null);
          setResolutionError(null);
        }
        return;
      }

      if (!cancelled) {
        setStatus("resolving");
        setNoAccessDetails(null);
        setResolutionError(null);
      }

      const accessToken =
        (user as { access_token?: string }).access_token ?? undefined;
      if (!accessToken) {
        if (!cancelled) {
          setUserId(null);
          setStatus("no-access");
          setNoAccessDetails({ reason: "missing-access-token" });
        }
        return;
      }

      try {
        const response = await fetch(atomUserEndpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!response.ok) {
          const detail = await readResponseDetail(response);
          if (!cancelled) {
            setUserId(null);
            if (response.status === 401 || response.status === 403) {
              setStatus("no-access");
              setNoAccessDetails({
                reason: response.status === 403 ? "forbidden" : "access-denied",
                statusCode: response.status,
                detail,
              });
            } else {
              setStatus("error");
              setResolutionError(
                new Error(
                  `Failed to resolve app user id from backend (status ${response.status}).`,
                ),
              );
            }
          }
          return;
        }
        const data = (await response.json()) as { userId?: string };
        const resolvedUserId =
          (data.userId as UserPublicID | undefined) ?? null;
        if (!cancelled) {
          if (resolvedUserId) {
            setUserId(resolvedUserId);
            setStatus("resolved");
          } else {
            setUserId(null);
            setStatus("no-access");
            setNoAccessDetails({
              reason: "missing-local-user-mapping",
              detail:
                "No local user mapping exists for the authenticated account.",
            });
          }
        }
      } catch (e) {
        console.error("Failed to resolve atom user id from backend", e);
        if (!cancelled) {
          setUserId(null);
          setStatus("error");
          setResolutionError(
            e instanceof Error
              ? e
              : new Error("Unknown error while resolving app user id."),
          );
        }
      }
    };

    void syncUserId();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, setUserId, atomUserEndpoint]);

  // automatically sign-in
  useEffect(() => {
    const signIn = async () => {
      if (
        !disableAuthFlows &&
        !hasAuthParams() &&
        !isAuthenticated &&
        !activeNavigator &&
        !isLoading &&
        !hasTriedSignin &&
        !error
      ) {
        try {
          await signinRedirect();
        } catch (e) {
          console.error("Auto Sign-in failed", e);
        } finally {
          setHasTriedSignin(true);
        }
      }
    };

    void signIn();
  }, [
    disableAuthFlows,
    hasTriedSignin,
    isAuthenticated,
    activeNavigator,
    isLoading,
    signinRedirect,
    error,
  ]);

  useEffect(() => {
    if (disableAuthFlows) {
      return;
    }
    const removeAccessTokenExpiring = events.addAccessTokenExpiring(
      async () => {
        try {
          await signinSilent();
        } catch (e) {
          console.error("access token renew failed", e);
        }
      },
    );
    const removeAccessTokenExpired = events.addAccessTokenExpired(() => {
      console.debug("access token expired");
      void signinRedirect();
    });

    return () => {
      removeAccessTokenExpiring();
      removeAccessTokenExpired();
    };
  }, [events, signinSilent, signinRedirect, disableAuthFlows]);

  useEffect(() => {
    if (error) {
      if (error.message.includes("No matching state found in storage")) {
        // some users may have a bookmark with an invalid state parameter
        // this causes an error, so we need to sign out and redirect to the login page
        // to definitely get a new + valid state parameter
        void signoutRedirect({
          post_logout_redirect_uri: globalThis.location.origin,
        });
      }
      console.error("oidc error", error);
    }
  }, [error, signoutRedirect]);

  return {
    status,
    noAccessDetails,
    error: resolutionError,
  };
};

async function readResponseDetail(
  response: Response,
): Promise<string | undefined> {
  try {
    const bodyText = (await response.text()).trim();
    if (!bodyText) {
      return undefined;
    }
    return bodyText;
  } catch {
    return undefined;
  }
}
